import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useSelector } from 'react-redux';
import { createSectionByIdSelector } from '../../../../redux/pages/selectors';
import TableContainer from './subComponents/TableContainer.component';
import { greys, mainColors } from '../../../../styling/theme';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useStatusChangeQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { StatusChangeTable } from '../../../../api/rs-hg/risk/statusChange.types';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

const useStyles = makeStyles(() => ({
  lookbackDaysContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '0.62rem',
    padding: '0.62rem 0.94rem',
    margin: '0.62rem',
    background: 'white',
    borderRadius: '0.25rem',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.62rem',
  },
  lookbackDaysTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: mainColors.mainBlue,
  },
  lookbackDaysInput: {
    all: 'unset',
    padding: '0.31rem',
    width: '2.50rem',
    height: '1.25rem',
    fontSize: '0.88rem',
    borderRadius: '0.12rem',
    border: `1px solid ${greys.grey200}`,
  },
  lookbackDaysButton: {
    all: 'unset',
    height: '1.25rem',
    padding: '0.31rem 0.62rem',
    borderRadius: '0.25rem',
    fontSize: '0.88rem',
    fontWeight: 500,
    background: mainColors.mainBlue,
    color: 'white',
    cursor: 'pointer',
    '&:hover': {
      background: mainColors.mainBlue_lighter,
    },
  },
  lookbackDaysActive: {
    fontSize: '0.88rem',
    color: mainColors.mainBlue,
    fontWeight: 500,
  },
}));

const handleValueChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setValue: React.Dispatch<React.SetStateAction<number | ''>>,
) => {
  const value = e.target.value;
  const number = Number(value);
  if (value === '') {
    setValue('');
  } else if (!isNaN(number) && number <= 180) {
    setValue(number);
  } else if (!isNaN(number) && number > 180) {
    setValue(180);
  }
};

const StatusChange: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props
) => {
  const classes = useStyles();

  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;

  const [lookbackDays, setLookbackDays] = React.useState<number | ''>(1);
  const [activeLookbackDays, setActiveLookbackDays] = React.useState<number>(1);

  const { data, isLoading, isFetching, isError, error } = useStatusChangeQuery({
    fundTypes,
    daysBack: activeLookbackDays,
  });

  return (
    <>
      <div className={classes.lookbackDaysContainer}>
        <form
          className={classes.formContainer}
          onSubmit={(e) => {
            e.preventDefault();
            setActiveLookbackDays(lookbackDays as number);
          }}
        >
          <div className={classes.lookbackDaysTitle}>Lookback Days:</div>
          <input
            type="text"
            className={classes.lookbackDaysInput}
            value={lookbackDays}
            onChange={(e) => handleValueChange(e, setLookbackDays)}
          />
          <button className={classes.lookbackDaysButton}>Calculate</button>
        </form>
        <div className={classes.lookbackDaysActive}>
          {`Active lookback days: ${activeLookbackDays}`}
        </div>
      </div>
      <QueryBoundary<StatusChangeTable[]>
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        error={error}
        data={data}
        loadingMessages={[
          `Loading data for ${activeLookbackDays} ${activeLookbackDays <= 1 ? 'day' : 'days'
          } previously...`,
        ]}
      >
        {(statusChangeData) => (
          <TableContainer data={statusChangeData} section={props.section} />
        )}
      </QueryBoundary>
    </>
  );
};

export default StatusChange;
